import { createContext, useCallback, useEffect, useState, useContext, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import global from "../../config/Global.json";
import { useNavigate, useLocation } from 'react-router-dom';
import "react-toastify/dist/ReactToastify.css";

const AuthContext = createContext({
  user: {},
  loginUser: () => { },
  logOutUser: () => { },
  authToken: null,
  setManualAuthToken: () => { },
  permissions: [],
});

export default AuthContext;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const getAPI = useCallback((API_NAME) => {
    return global.api.host + global.api[API_NAME];
  }, []);

  const [authToken, setAuthToken] = useState(() =>
    localStorage.getItem("authToken") ? JSON.parse(localStorage.getItem("authToken")) : null
  );

  const [user, setUser] = useState(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const parsedToken = JSON.parse(token);
        return parsedToken.access ? jwtDecode(parsedToken.access) : {};
      } catch (error) {
        console.error("Error decoding token:", error);
        localStorage.removeItem("authToken");
        return {};
      }
    }
    return {};
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // ── AXIOS 401 INTERCEPTOR ──
  const interceptorRef = useRef(null);
  const initialRefreshStartedRef = useRef(false);
  const refreshInFlightRef = useRef(null);
  const refreshAttemptedForAccessRef = useRef(null);

  useEffect(() => {
    // Eject previous interceptor before adding new one
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const currentAccessToken = JSON.parse(localStorage.getItem('authToken') || '{}')?.access;
        const canAttemptRefresh =
          Boolean(refreshInFlightRef.current) ||
          (currentAccessToken &&
            refreshAttemptedForAccessRef.current !== currentAccessToken);

        // A token may be revoked or rejected before its JWT expiry, so allow one
        // recovery refresh for each access token. Marking both the rejected and
        // replacement tokens prevents permanently unauthorized endpoints from
        // creating an application-wide refresh/request loop.
        if (
          error.response?.status === 401 &&
          canAttemptRefresh &&
          !originalRequest._retry &&
          !originalRequest.url?.includes(global.api.token) &&
          !originalRequest.url?.includes(global.api.refreshToken)
        ) {
          originalRequest._retry = true;
          refreshAttemptedForAccessRef.current = currentAccessToken;

          const storedRefresh =
            localStorage.getItem('refresh_token') ||
            JSON.parse(localStorage.getItem('authToken') || '{}')?.refresh;

          if (storedRefresh) {
            try {
              if (!refreshInFlightRef.current) {
                refreshInFlightRef.current = axios.post(
                  global.api.host + global.api.refreshToken,
                  { refresh: storedRefresh }
                ).then((res) => {
                  const refreshedToken = {
                    ...res.data,
                    refresh: res.data.refresh || storedRefresh,
                  };

                  // Apply a shared refresh result once. Previously every 401
                  // waiter repeated these state updates, causing application
                  // consumers to refetch in a cascade.
                  refreshAttemptedForAccessRef.current = refreshedToken.access;
                  setAuthToken(refreshedToken);
                  setUser(jwtDecode(refreshedToken.access));
                  localStorage.setItem('authToken', JSON.stringify(refreshedToken));
                  localStorage.setItem('refresh_token', refreshedToken.refresh);
                  return refreshedToken;
                }).finally(() => { refreshInFlightRef.current = null; });
              }
              const newToken = await refreshInFlightRef.current;
              // Retry original request with new access token
              originalRequest.headers['Authorization'] = `Bearer ${newToken.access}`;
              return axios(originalRequest);
            } catch {
              logOutUser('Session expired. Please log in again.');
              return Promise.reject(error);
            }
          } else {
            logOutUser('Session expired. Please log in again.');
          }
        }

        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [authToken]);
  const fetchPermissions = async () => {
    try {
      const response = await axios.get(getAPI("getPermissions"), {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
        },
      });
      if (response.data?.permissions) {
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      console.error("Permission fetch failed", error);
    }
  };

  useEffect(() => {
    const checkAutoLogin = async () => {

      if (location.pathname.includes('/autologin/')) {
        const pathParts = location.pathname.split("/");
        const refreshToken = pathParts[pathParts.length - 1];

        if (refreshToken && refreshToken !== "autologin") {
          try {

            localStorage.removeItem("authToken");

            localStorage.setItem("refresh_token", refreshToken);

            const response = await axios.post(
              getAPI("refreshToken"),
              { refresh: refreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );

            if (response.data && response.data.access) {

              setAuthToken(response.data);
              setUser(jwtDecode(response.data.access));
              localStorage.setItem("authToken", JSON.stringify(response.data));

              toast.success("Auto-login successful!");

              setTimeout(() => navigate("/"), 1000);
            }
          } catch (error) {
            console.error("Auto-login failed:", error);
            toast.error("Auto-login failed. Please log in manually.");
            navigate("/login");
          }
        }
      }
    };

    checkAutoLogin();
  }, [location.pathname, navigate, getAPI]);

  const loginUser = async (e) => {
    e.preventDefault();

  try {
    // Clear any existing cached data before login
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("cache_") || 
            key.endsWith("checkUserType") ||
            key === "currentApp" ||
            key === "defaultApp" ||
            key === "allowedFeatures" ||
            key === "subscriptionActive") {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Error clearing cache before login:", error);
    }

    const response = await axios.post(getAPI("token"), {
      username: e.target.username.value,
      password: e.target.password.value,
    });

      const tokenData = response.data;

      if (!tokenData.access || !tokenData.refresh) {
        toast.error("Invalid token response");
        return;
      }
      localStorage.setItem("authToken", JSON.stringify(tokenData));
      localStorage.setItem("refresh_token", tokenData.refresh);
      setAuthToken(tokenData);
      setUser(jwtDecode(tokenData.access));

      if (global.checkSuperUser === true) {
        await checkSuperUser(tokenData.access);
      }
      await fetchPermissions();
      toast.success("Login successful! Welcome back!");
      navigate("/");

    } catch (error) {
      if (error.response?.data?.detail === "No active account found with the given credentials") {
        toast.error("Username or password incorrect. Please try again.");
      } else {
        toast.error("Login failed. Please try again.");
      }
      console.error("Login error:", error);
    }
  };

  const setManualAuthToken = (tokenData) => {
    if (!tokenData || !tokenData.access) {
      console.error("Invalid token data:", tokenData);
      return;
    }

    setAuthToken(tokenData);
    setUser(jwtDecode(tokenData.access));
    localStorage.setItem("authToken", JSON.stringify(tokenData));
    localStorage.setItem("refresh_token", tokenData.refresh);
  };

  const checkSuperUser = async (token) => {
    if (!token) {
      toast.error("Invalid token");
      return;
    }

    try {
      const response = await axios.get(getAPI("isSuperUser"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.is_superuser) {

        const authData = { access: token, refresh: localStorage.getItem("refresh_token") };
        setAuthToken(authData);
        setUser(jwtDecode(token));
        localStorage.setItem("authToken", JSON.stringify(authData));
        toast.success("Welcome, Admin");
        navigate("/");
      } else {
        logOutUser("User is not super user");
      }
    } catch (error) {
      toast.error("Error checking user permissions");
      console.error(error);
    }
  };

  const logOutUser = useCallback((message) => {
    // Clear all user-specific cached data
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("cache_") || 
            key.endsWith("checkUserType") ||
            key === "currentApp" ||
            key === "defaultApp" ||
            key === "allowedFeatures" ||
            key === "subscriptionActive") {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }

    // Clear auth tokens
    localStorage.removeItem("authToken");
    localStorage.removeItem("refresh_token");

    // Reset state
    setAuthToken(null);
    setUser({});
    setPermissions([]);

    // Show message if provided
    if (message) {
      toast.error(message);
    }

    // Dispatch custom event for other components to cleanup (after a small delay)
    setTimeout(() => {
      try {
        window.dispatchEvent(new Event('userLoggedOut'));
      } catch (error) {
        console.error("Error dispatching logout event:", error);
      }
    }, 100);

    // Navigate to login
    navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (authToken?.access) {
      fetchPermissions();
    }
  }, [authToken]);

  useEffect(() => {
    const checkSuperUserRefresh = async (token) => {
      if (!token) return;

      try {
        const response = await axios.get(getAPI("isSuperUser"), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.data.is_superuser) {
          logOutUser("User is not super user");
        }
      } catch (error) {
        console.error("Error checking user permissions:", error);

        if (!loading) {
          toast.error("Error checking user permissions");
        }
      }
    };

    const refreshToken = () => {
      const storedRefreshToken = localStorage.getItem("refresh_token") ||
        (authToken?.refresh ? authToken.refresh : null);

      if (storedRefreshToken) {
        axios
          .post(getAPI("refreshToken"), { refresh: storedRefreshToken })
          .then((response) => {
            if (response.data && response.data.access) {
              if (global.checkSuperUser !== undefined && global.checkSuperUser === true) {
                checkSuperUserRefresh(response.data.access);
              }

              const nextToken = {
                ...response.data,
                refresh: response.data.refresh || storedRefreshToken,
              };
              setAuthToken(nextToken);
              setUser(jwtDecode(response.data.access));
              localStorage.setItem("authToken", JSON.stringify(nextToken));
              localStorage.setItem("refresh_token", nextToken.refresh);
            } else {
              throw new Error("Invalid response data");
            }
          })
          .catch((error) => {
            console.error("Token refresh failed:", error);
            logOutUser();
          })
          .finally(() => {
            if (loading) {
              setLoading(false);
            }
          });
      } else if (authToken) {

        if (authToken.refresh) {
          localStorage.setItem("refresh_token", authToken.refresh);
          refreshToken();
        } else {
          logOutUser();
          setLoading(false);
        }
      } else {

        setLoading(false);
      }
    };

    if (loading && !initialRefreshStartedRef.current) {
      initialRefreshStartedRef.current = true;
      refreshToken();
    }

    const fourMinutes = 1000 * 60 * 4;
    const interval = setInterval(() => {
      if (authToken) {
        refreshToken();
      }
    }, fourMinutes);
    return () => clearInterval(interval);
  }, [authToken, loading, logOutUser, getAPI]);
  const contextData = {
    user,
    loginUser,
    logOutUser,
    authToken,
    setManualAuthToken,
    permissions,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {loading ? null : children}
      {/* <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      /> */}
    </AuthContext.Provider>
  );
};
